import { SButton } from "@superwall-me/sui"
import { useEffect, useState } from "react"
import { Header } from "./splash-sections/Header"
import { Customers } from "./splash-sections/Customers"
import { CodeSnippet } from "./splash-sections/CodeSnippet"
import { Templates } from "./splash-sections/Templates"
import { Triggers } from "./splash-sections/Triggers"
import { Campaigns } from "./splash-sections/Campaigns"
import { Reviews } from "./splash-sections/Reviews"
import { Investors } from "./splash-sections/Investors"
import { Pricing } from "./splash-sections/Pricing"
import { Routes } from "@blitzjs/next"
import useUtmParams from "app/core/hooks/useUTMParams"
import useAnalytics from "app/analytics/hooks/useAnalytics"
import { TAnalyticsEvent } from "@superwall/legacy-api-utils"
import { Post } from "integrations/contentful"
import { useSession } from "app/authv2/use-session"

export const SignUpButton = ({
  className = "",
  showBookDemo = false,
  source = "main-cta",
  mainCta,
  bookDemoCta,
}: {
  className?: string
  showBookDemo?: Boolean
  mainCta?: React.ReactNode
  bookDemoCta?: React.ReactNode
  source: "main-cta" | "pricing-cta" | "header" | "blog-home" | "blog-post" | "404-page"
}) => {
  const track = useAnalytics()
  const { isLoggedIn, isLoading } = useSession()
  const { utmParams } = useUtmParams()

  return isLoggedIn ? (
    <>
      <SButton
        href={Routes.Dashboard()}
        variant="juicy"
        size="xl"
        className={`lg:mx-0 !px-7 !py-3 !rounded-full ${className || ""}`}
      >
        <span className="tracking-tight text-xl">
          Dashboard <span className="ml-1 lg:ml-2">→</span>
        </span>
      </SButton>
    </>
  ) : (
    <>
      <SButton
        variant="juicy"
        size="xl"
        // href={Routes.SignupPage(utmParams)}
        href={`/api/auth/signup`}
        className={`!px-7 whitespace-nowrap !py-3 !rounded-full ${className || ""}`}
        onClick={() => {
          track({
            name: "SignUp_Begin",
            properties: {
              source,
            },
            group: {
              kind: "none",
              object: {},
            },
          } as TAnalyticsEvent)
        }}
      >
        <span className="tracking-tight text-xl">{mainCta ? mainCta : <>Try for free</>}</span>
      </SButton>
      {showBookDemo ? (
        <SButton
          variant="juicy"
          isMinimal
          size="xl"
          className={`!px-0 !py-0`}
          href={"/contact/book-a-demo"}
          target="_blank"
          onClick={() => {
            track({
              name: "DemoRequest_Begin",
              properties: {
                source,
              },
              group: {
                kind: "none",
                object: {},
              },
            } as TAnalyticsEvent)
          }}
        >
          <span className="inline-flex items-center whitespace-nowrap gap-3">
            <span
              className="tracking-tight text-xl text-brand-primary "
              style={{
                textDecoration: "underline",
                textUnderlineOffset: "4px",
                textDecorationColor: "#75FFF1",
                textDecorationThickness: "3px",
                color: "rgba(255,255,255,0.9)",
              }}
            >
              {" "}
              {bookDemoCta ? bookDemoCta : "Book a demo"}
            </span>{" "}
            <div
              className="hidden md:inline"
              style={
                isLoading
                  ? {
                      transform: "scale(0)",
                      transformOrigin: "left",
                      opacity: 0,
                      transition: "all 600ms ease-in-out 1s",
                    }
                  : {
                      transform: "scale(1)",
                      transformOrigin: "left",
                      opacity: 1,
                      transition: "all 600ms ease-in-out 1s",
                    }
              }
            >
              <div className="text-accent-light waving-hand-home mt-1">{WavingHand}</div>
            </div>
          </span>
        </SButton>
      ) : null}
    </>
  )
}

export const BookDemoButton = ({
  className = "",
  source = "main-cta",
  mainCta,
  bookDemoCta,
}: {
  className?: string
  mainCta?: React.ReactNode
  bookDemoCta?: React.ReactNode
  source: "main-cta" | "pricing-cta" | "header" | "blog-home" | "blog-post" | "404-page"
}) => {
  const track = useAnalytics()

  return (
    <SButton
      variant="juicy"
      size="xl"
      href={
        source === "pricing-cta" ? "/contact/book-a-demo?meetingType=sales" : "/contact/book-a-demo"
      }
      target="_blank"
      onClick={() => {
        track({
          name: "DemoRequest_Begin",
          properties: {
            source,
          },
          group: {
            kind: "none",
            object: {},
          },
        } as TAnalyticsEvent)
      }}
      className={`!px-7 whitespace-nowrap !py-3 !rounded-full ${className || ""}`}
    >
      <span className="tracking-tight text-xl">{mainCta ? mainCta : <>Try for free</>}</span>
    </SButton>
  )
}

export const Main = ({ post }: { post?: Post }) => {
  const [loaded, setLoaded] = useState(false)

  // const timer = setInterval(() => {
  //   setLoaded(!loaded)
  // }, 3000)

  // on mount
  useEffect(() => {
    setLoaded(true)
    return () => {
      // on unmount
      setLoaded(false)
    }
  }, [])

  return (
    <>
      <main className="mx-auto relative pt-32 overflow-hidden px-[16px] md:px-5">
        <Header post={post} />
        <Customers />
        <CodeSnippet />
        <Templates />
        <Triggers />
        <Campaigns />
        <Reviews />
        <Pricing />
        <Investors />
      </main>
    </>
  )
}

const WavingHand = (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 48 48">
    <title>waving-hand</title>
    <g>
      <path
        d="M37.843,8.814a2.851,2.851,0,0,0-2.49,3.362c.027.224.69,5.166,1.353,8.308L22.829,6.607a2.5,2.5,0,0,0-3.708.192,2.6,2.6,0,0,0,.273,3.445l8.032,8.031a1,1,0,0,1-1.415,1.414L15.051,8.729a2.5,2.5,0,0,0-3.708.191,2.6,2.6,0,0,0,.273,3.445l10.86,10.859a1,1,0,0,1-1.414,1.415L10.909,14.486a2.6,2.6,0,0,0-3.445-.273,2.5,2.5,0,0,0-.191,3.708L17.526,28.174a1,1,0,0,1-1.414,1.414L9.495,22.972A2.6,2.6,0,0,0,6.05,22.7a2.5,2.5,0,0,0-.191,3.708l8.7,8.7,5.8,5.8a14,14,0,0,0,19.8,0l1.735-1.736a13.962,13.962,0,0,0,3.391-14.3c-1.465-4.377-3.545-9.312-4.542-13.795A2.832,2.832,0,0,0,37.843,8.814Z"
        fill="#ffd764"
      ></path>{" "}
      <path
        d="M35.209,34.533a1,1,0,0,1-1.415,0c-3.9-3.9-3.581-10.56.707-14.849A1,1,0,1,1,35.916,21.1c-3.51,3.509-3.827,8.9-.707,12.021A1,1,0,0,1,35.209,34.533Z"
        fill="#e2ac4b"
      ></path>{" "}
      <path
        d="M35,8a1,1,0,0,1-.858-.485A9.978,9.978,0,0,0,26,3a1,1,0,0,1,0-2,12.007,12.007,0,0,1,9.857,5.485A1,1,0,0,1,35,8Z"
        fill="#b3b3b3"
      ></path>{" "}
      <path
        d="M31,11a1,1,0,0,1-.948-.684A3.44,3.44,0,0,0,27,8a1,1,0,0,1,0-2c3.679,0,4.9,3.533,4.948,3.684A1,1,0,0,1,31,11Z"
        fill="#b3b3b3"
      ></path>{" "}
      <path
        d="M10,41a.992.992,0,0,1-.143-.01c-.077-.011-7.754-1.2-8.848-8.848a1,1,0,1,1,1.98-.284c.878,6.15,6.9,7.115,7.155,7.152A1,1,0,0,1,10,41Z"
        fill="#b3b3b3"
      ></path>{" "}
      <path
        d="M10,36a1,1,0,0,1-.316-.051,7.817,7.817,0,0,1-4.632-4.633,1,1,0,1,1,1.9-.632,5.791,5.791,0,0,0,3.368,3.367A1,1,0,0,1,10,36Z"
        fill="#b3b3b3"
      ></path>
    </g>
  </svg>
)

export default Main
